import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import PortalAdminCredentialsModal from '../../components/PortalAdminCredentialsModal';
import { useNotification } from '../../context/NotificationContext';
import {
  getSalons,
  createSalon,
  updateSalon,
  deleteSalon,
  getAdminServices,
  mapServicesToSalon,
  getBranchAdmin,
  upsertBranchAdmin
} from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import ExpandableText from '../../components/ExpandableText';
import { getUpiIdError } from '../../utils/upiValidation';
import { getTenDigitPhoneError, toTenDigitPhone } from '../../utils/phone';
import usePortalScope from '../../hooks/usePortalScope';
import { removeConfirmMessage } from '../../utils/portalCopy';
import AdminFormSection from '../../components/AdminFormSection';
import { useAuth } from '../../context/AuthContext';
import { tenantListParams } from '../../utils/contentScope';
import './AdminPages.css';

const SalonManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const { refreshPortalContext } = useAuth();
  const portal = usePortalScope();
  const canManageBranchAdmins = portal.canManageBranchAdmins;
  const canCreateBranch = portal.canCreateBranch;
  const canDeleteBranch = portal.canDeleteBranch;
  const brands = portal.brands;
  const filterSalons = portal.salons;
  const [showForm, setShowForm] = useState(false);
  const [editingSalon, setEditingSalon] = useState(null);
  const [showMappingPanel, setShowMappingPanel] = useState(false);
  const [mappingSalon, setMappingSalon] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    brandId: portal.lockBrandId || '',
    salonId: portal.lockSalonId || ''
  });
  const [adminTarget, setAdminTarget] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [formData, setFormData] = useState({
    brand: portal.lockBrandId || '',
    branchCode: '',
    name: '',
    branch: '',
    address: { street: '', city: '', state: '', zipCode: '', country: '' },
    contact: { phone: '', email: '' },
    payment: { upiId: '', accountName: '', accountNumber: '', ifscCode: '' }
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!portal.lockBrandId && !portal.lockSalonId) return;
    setFilters((prev) => {
      const brandId = portal.lockBrandId || prev.brandId || '';
      const salonId = portal.lockSalonId || prev.salonId || '';
      if (prev.brandId === brandId && prev.salonId === salonId) return prev;
      return { ...prev, brandId, salonId };
    });
  }, [portal.lockBrandId, portal.lockSalonId]);

  const searchKey = useMemo(
    () => JSON.stringify({ searchTerm, ...filters }),
    [searchTerm, filters]
  );

  const getSalonListParams = useCallback(() => {
    const params = { ...tenantListParams(portal, filters) };
    if (searchTerm) params.search = searchTerm;
    return params;
  }, [searchTerm, filters, portal]);

  const { items: salons, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getSalons,
    resetKey: searchKey,
    getParams: getSalonListParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(salons, (salon) => salon._id, {
    defaultView: 'grid',
    resetKey: searchKey
  });

  const suggestBranchSuffix3 = (value) => {
    const v = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return (v.slice(0, 3) || 'BRN').padEnd(3, 'X').slice(0, 3);
  };

  const suggestBrandCode2 = (name) => {
    const words = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const a = (words[0]?.[0] || '').toUpperCase();
    const b = (words[1]?.[0] || words[0]?.[1] || '').toUpperCase();
    const raw = `${a}${b}`.replace(/[^A-Z0-9]/g, '');
    return (raw || 'BC').padEnd(2, 'X').slice(0, 2);
  };

  const validatePhone = (phone) => getTenDigitPhoneError(phone, { required: true });
  const validateEmail = (email) => {
    if (!email || !String(email).trim()) return '';
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).trim()) ? '' : 'Enter a valid email address';
  };
  const validateRequired = (value, fieldName) => {
    if (!value || !String(value).trim()) return `${fieldName} is required`;
    return '';
  };
  const validateLettersOnly = (value, fieldName) => {
    const v = String(value || '').trim();
    if (!v) return `${fieldName} is required`;
    // Allow letters with spaces/dots/hyphens/apostrophes (e.g., "New Delhi", "St. John's")
    const re = /^[A-Za-z][A-Za-z\s.'-]*$/;
    return re.test(v) ? '' : `${fieldName} should contain only letters`;
  };

  const validateForm = () => {
    const err = {};
    err.brand = validateRequired(formData.brand, 'Brand');
    err.name = validateRequired(formData.name, 'Branch name');
    err.city = validateLettersOnly(formData.address?.city, 'City');
    err.state = validateLettersOnly(formData.address?.state, 'State');
    err.country = validateLettersOnly(formData.address?.country, 'Country');
    err.phone = validatePhone(formData.contact?.phone);
    const branchId = String(formData.branchCode || '').trim().toUpperCase();
    if (!branchId) {
      err.branchCode = 'Branch ID is required';
    } else if (!/^([A-Z0-9]{3}|[A-Z0-9]{2}-[A-Z0-9]{3})$/.test(branchId)) {
      err.branchCode = 'Branch ID must be like HSR or BC-HSR';
    }
    const pin = String(formData.address?.zipCode || '').replace(/\D/g, '');
    if (!pin) {
      err.zipCode = 'Pincode is required';
    } else if (pin.length !== 6) {
      err.zipCode = 'Pincode must be 6 digits';
    }
    const emailErr = validateEmail(formData.contact?.email);
    if (emailErr) err.email = emailErr;

    const upiErr = getUpiIdError(formData.payment?.upiId);
    if (upiErr) err.upiId = upiErr;
    setFormErrors(err);
    return !Object.values(err).some(Boolean);
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await getAdminServices({ all: true });
      const { data } = normalizeAdminListResponse(response);
      setAllServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleUpiBlur = () => {
    const upiErr = getUpiIdError(formData.payment?.upiId);
    setFormErrors((prev) => ({ ...prev, upiId: upiErr }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showError('Please fix the highlighted fields and try again.');
      // Bring the first invalid field into view for fast correction.
      window.requestAnimationFrame(() => {
        const firstInvalid = document.querySelector('.admin-page-container .input-error');
        firstInvalid?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        firstInvalid?.focus?.({ preventScroll: true });
      });
      return;
    }
    try {
      if (editingSalon) {
        await updateSalon(editingSalon._id, formData);
      } else {
        await createSalon(formData);
      }
      setShowForm(false);
      setEditingSalon(null);
      resetForm();
      showSuccess(editingSalon ? 'Branch updated.' : 'Branch created.');
      await refreshPortalContext();
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || `Failed to ${editingSalon ? 'update' : 'create'} branch`);
    }
  };

  const resetForm = () => {
    setFormErrors({});
    setFormData({
      brand: portal.lockBrandId || '',
      branchCode: '',
      name: '',
      branch: '',
      address: { street: '', city: '', state: '', zipCode: '', country: '' },
      contact: { phone: '', email: '' },
      payment: { upiId: '', accountName: '', accountNumber: '', ifscCode: '' }
    });
  };

  const handleEdit = (salon) => {
    setEditingSalon(salon);
    const existingBranchCode = String(salon.branchCode || '').trim().toUpperCase();
    const brandCode =
      String(salon.brand?.brandCode || '').trim().toUpperCase() ||
      suggestBrandCode2(salon.brand?.name);
    const suffix = suggestBranchSuffix3(salon.branch || salon.name);
    const fallbackBranchCode = brandCode ? `${brandCode}-${suffix}` : '';
    setFormData({
      brand: salon.brand?._id || salon.brand || '',
      // Backfill for older branches created before `branchCode` existed.
      branchCode: existingBranchCode || fallbackBranchCode,
      name: salon.name || '',
      branch: salon.branch || '',
      address: {
        street: salon.address?.street || '',
        city: salon.address?.city || '',
        state: salon.address?.state || '',
        zipCode: salon.address?.zipCode || '',
        country: salon.address?.country || ''
      },
      contact: {
        phone: toTenDigitPhone(salon.contact?.phone),
        email: salon.contact?.email || ''
      },
      payment: {
        upiId: salon.payment?.upiId || '',
        accountName: salon.payment?.accountName || '',
        accountNumber: salon.payment?.accountNumber || '',
        ifscCode: salon.payment?.ifscCode || ''
      }
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove branch',
      message: removeConfirmMessage('branch'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deleteSalon(id);
      showSuccess('Branch removed.');
      await refreshPortalContext();
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove branch');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSalon(null);
    resetForm();
  };

  const openBranchAdmin = async (salon) => {
    setAdminTarget(salon);
    setAdminData(null);
    setAdminLoading(true);
    try {
      const res = await getBranchAdmin(salon._id);
      setAdminData(res.data?.admin || null);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to load branch admin');
      setAdminTarget(null);
    } finally {
      setAdminLoading(false);
    }
  };

  const closeBranchAdmin = () => {
    if (adminSaving) return;
    setAdminTarget(null);
    setAdminData(null);
  };

  const saveBranchAdmin = async (payload) => {
    if (!adminTarget?._id) return;
    setAdminSaving(true);
    try {
      const res = await upsertBranchAdmin(adminTarget._id, payload);
      setAdminData(res.data?.admin || null);
      showSuccess(adminData?.id ? 'Branch admin credentials updated.' : 'Branch admin created.');
      setAdminTarget(null);
      setAdminData(null);
    } catch (error) {
      throw error;
    } finally {
      setAdminSaving(false);
    }
  };

  const isMappedToSalon = (service, salonId) => {
    const salons = Array.isArray(service?.salons) ? service.salons : [];
    return salons.some((s) => (typeof s === 'object' ? s?._id : s)?.toString() === salonId?.toString());
  };

  const getMappedServiceCount = (salonId) => {
    return allServices.filter((service) => isMappedToSalon(service, salonId)).length;
  };

  const openServiceMapping = (salon) => {
    const preSelected = allServices
      .filter((service) => isMappedToSalon(service, salon._id))
      .map((service) => String(service._id ?? ''));
    setMappingSalon(salon);
    setSelectedServiceIds(preSelected.filter(Boolean));
    setShowMappingPanel(true);
  };

  const closeServiceMapping = () => {
    setShowMappingPanel(false);
    setMappingSalon(null);
    setSelectedServiceIds([]);
  };

  const handleServiceSelection = (serviceId) => {
    const id = String(serviceId);
    if (!id) return;
    setSelectedServiceIds((prev) =>
      prev.some((s) => String(s) === id)
        ? prev.filter((s) => String(s) !== id)
        : [...prev, id]
    );
  };

  const handleSaveServiceMapping = async () => {
    if (!mappingSalon?._id) return;
    setMappingLoading(true);
    try {
      await mapServicesToSalon(mappingSalon._id, selectedServiceIds);
      showSuccess('Branch service mapping updated.');
      await fetchServices();
      await reload();
      closeServiceMapping();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to map services to branch');
    } finally {
      setMappingLoading(false);
    }
  };

  const handleCopySalonId = async (salonId) => {
    const idStr = String(salonId ?? '');
    if (!idStr) return;
    try {
      // Prefer modern Clipboard API when available
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(idStr);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = idStr;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showSuccess('Branch ID copied to clipboard.');
    } catch (error) {
      showError('Failed to copy Branch ID');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      brandId: portal.lockBrandId || '',
      salonId: portal.lockSalonId || ''
    });
  };

  const getDisplayBranchCode = (salon) => {
    const existing = String(salon.branchCode || '').trim().toUpperCase();
    const brandCode =
      String(salon.brand?.brandCode || '').trim().toUpperCase() ||
      suggestBrandCode2(salon.brand?.name);
    const suffix = suggestBranchSuffix3(salon.branch || salon.name);
    const fallback = brandCode ? `${brandCode}-${suffix}` : '';
    return existing || fallback;
  };

  const salonColumns = useMemo(
    () => [
      { key: 'name', label: 'Branch Name', render: (salon) => <strong>{salon.name}</strong> },
      {
        key: 'branchCode',
        label: 'Branch ID',
        render: (salon) => getDisplayBranchCode(salon) || 'N/A'
      },
      { key: 'brand', label: 'Brand', render: (salon) => salon.brand?.name || 'N/A' },
      { key: 'branch', label: 'Location', render: (salon) => salon.branch || '-' },
      { key: 'city', label: 'City', render: (salon) => salon.address?.city || '-' },
      { key: 'mirrors', label: 'Mirrors', render: (salon) => salon.mirrors?.length || 0 },
      {
        key: 'services',
        label: 'Mapped Services',
        render: (salon) => getMappedServiceCount(salon._id)
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (salon) => (
          <div className="table-actions">
            {canManageBranchAdmins && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => openBranchAdmin(salon)}>
                Admin Login
              </button>
            )}
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => openServiceMapping(salon)}>
              Map Services
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEdit(salon)}>
              Edit
            </button>
            {canDeleteBranch && (
              <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(salon._id)}>
                Remove
              </button>
            )}
          </div>
        )
      }
    ],
    [canManageBranchAdmins, canDeleteBranch, allServices]
  );

  const salonListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && salons.length > 0 && (
        <p className="admin-infinite-end">All branches loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const salonEmptyState = (
    <div className="empty-state">
      <p>No branches found. Create your first branch!</p>
    </div>
  );

  return (
    <AdminLayout
      pageTitle="Branch & Mirror Management"
      subtitle={
        pagination.total
          ? `${salons.length} of ${pagination.total} branches · scroll for more`
          : 'Manage branch locations and Alter Mirror devices'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <h1>Branch List</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search branches by name, location, brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {canCreateBranch && (
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setEditingSalon(null);
                  resetForm();
                  setShowForm(true);
                }}
              >
                + Add Branch
              </button>
            )}
          </div>
        </div>

        {(portal.showBrandPicker || portal.showBranchPicker) && (
        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={handleFilterChange}
              brands={brands}
              salons={filterSalons}
            />
            <div className="form-group filter-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>
        )}

      {loading ? (
        <Loader variant="dark" fullScreen={false} />
      ) : (
        <>
      {showForm && (
        <div className="admin-card" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, fontSize: '16px', color: '#FFFFFF', marginBottom: '20px' }}>
            {editingSalon ? 'Edit Branch' : 'Add Branch'}
          </h2>
          <p style={{ color: '#8189A5', fontSize: '12px', marginBottom: '16px' }}>
            Hierarchy: Brand → Branch → Mirrors. Select a brand, then add this branch; add mirrors under this branch from the Mirrors page.
          </p>
          <form onSubmit={handleSubmit} noValidate>
            <AdminFormSection title="Basics" defaultOpen>
            <div className="form-group">
              <label>Brand *</label>
              <select
                value={formData.brand}
                className={formErrors.brand ? 'input-error' : ''}
                disabled={Boolean(portal.lockBrandId)}
                onChange={(e) => {
                  const brandId = e.target.value;
                  const selected = brands.find((b) => String(b._id) === String(brandId));
                  const brandCode = (selected?.brandCode || suggestBrandCode2(selected?.name))
                    .toString()
                    .trim()
                    .toUpperCase();
                  setFormData((prev) => {
                    const current = String(prev.branchCode || '').trim().toUpperCase();
                    const currentPrefixMatch = current.match(/^([A-Z0-9]{2})-?$/);
                    const isOnlyPrefix = Boolean(currentPrefixMatch) || current === `${brandCode}-`;
                    // If user already has full "XX-YYY", keep it as-is.
                    if (current && /^[A-Z0-9]{2}-[A-Z0-9]{3}$/.test(current)) {
                      return { ...prev, brand: brandId };
                    }
                    // If user entered only "YYY", convert to "XX-YYY" on brand selection.
                    if (brandCode && /^[A-Z0-9]{3}$/.test(current)) {
                      return { ...prev, brand: brandId, branchCode: `${brandCode}-${current}` };
                    }
                    // If empty (or only a prefix), auto-populate prefix "XX-" so user can type the 3-char suffix.
                    if (brandCode && (!current || isOnlyPrefix)) {
                      return { ...prev, brand: brandId, branchCode: `${brandCode}-` };
                    }
                    return { ...prev, brand: brandId };
                  });
                }}
              >
                <option value="">Select a brand</option>
                {brands.map(brand => (
                  <option key={brand._id} value={brand._id}>{brand.name}</option>
                ))}
              </select>
              {formErrors.brand && <span className="form-error-text">{formErrors.brand}</span>}
            </div>

            <div className="form-group">
              <label>Branch Name *</label>
              <input
                type="text"
                value={formData.name}
                className={formErrors.name ? 'input-error' : ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {formErrors.name && <span className="form-error-text">{formErrors.name}</span>}
            </div>

            <div className="form-group">
              <label>Branch ID (HSR or BC-HSR)</label>
              <input
                type="text"
                value={formData.branchCode}
                onChange={(e) =>
                  setFormData({ ...formData, branchCode: (e.target.value || '').toUpperCase() })
                }
                placeholder="e.g. HSR or BC-HSR"
                maxLength={6}
                className={formErrors.branchCode ? 'input-error' : ''}
                disabled={Boolean(editingSalon)}
              />
              <small style={{ color: '#8189A5', fontSize: '12px' }}>
                Used to generate vendor-friendly mirror codes (e.g., BC-HSR-001).
              </small>
              {formErrors.branchCode && <span className="form-error-text">{formErrors.branchCode}</span>}
            </div>

            <div className="form-group">
              <label>Branch / Location</label>
              <input
                type="text"
                value={formData.branch}
                onChange={(e) => {
                  const branch = e.target.value;
                  setFormData((prev) => {
                    const selected = brands.find((b) => String(b._id) === String(prev.brand));
                    const brandCode = (selected?.brandCode || suggestBrandCode2(selected?.name))
                      .toString()
                      .trim()
                      .toUpperCase();
                    const current = String(prev.branchCode || '').trim().toUpperCase();
                    // Auto-suggest only when creating AND when branchCode is empty or only has prefix.
                    const canSuggest = !editingSalon && brandCode && (!current || current === `${brandCode}-`);
                    return {
                      ...prev,
                      branch,
                      branchCode: canSuggest ? `${brandCode}-${suggestBranchSuffix3(branch)}` : prev.branchCode
                    };
                  });
                }}
                placeholder="e.g. Downtown, Mall location name"
              />
            </div>

            </AdminFormSection>

            <AdminFormSection title="Address" defaultOpen>

            <div className="form-group">
              <label>Street</label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, street: e.target.value }
                })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value }
                  })}
                  className={formErrors.city ? 'input-error' : ''}
                />
                {formErrors.city && <span className="form-error-text">{formErrors.city}</span>}
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  value={formData.address.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, state: e.target.value }
                  })}
                  className={formErrors.state ? 'input-error' : ''}
                />
                {formErrors.state && <span className="form-error-text">{formErrors.state}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Pincode *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.address.zipCode}
                  onChange={(e) => {
                    const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 6);
                    setFormData({
                      ...formData,
                      address: { ...formData.address, zipCode: digits }
                    });
                  }}
                  placeholder="6-digit pincode"
                  maxLength={6}
                  className={formErrors.zipCode ? 'input-error' : ''}
                />
                {formErrors.zipCode && <span className="form-error-text">{formErrors.zipCode}</span>}
              </div>
              <div className="form-group">
                <label>Country *</label>
                <input
                  type="text"
                  value={formData.address.country}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, country: e.target.value }
                  })}
                  className={formErrors.country ? 'input-error' : ''}
                />
                {formErrors.country && <span className="form-error-text">{formErrors.country}</span>}
              </div>
            </div>

            </AdminFormSection>

            <AdminFormSection title="Contact" defaultOpen>

            <div className="form-row">
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={formData.contact.phone}
                  onChange={(e) => {
                    const digits = toTenDigitPhone(e.target.value);
                    setFormData({
                      ...formData,
                      contact: { ...formData.contact, phone: digits }
                    });
                  }}
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="10-digit mobile"
                  className={formErrors.phone ? 'input-error' : ''}
                />
                {formErrors.phone && <span className="form-error-text">{formErrors.phone}</span>}
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) => setFormData({
                    ...formData,
                    contact: { ...formData.contact, email: e.target.value }
                  })}
                  className={formErrors.email ? 'input-error' : ''}
                />
                {formErrors.email && <span className="form-error-text">{formErrors.email}</span>}
              </div>
            </div>

            </AdminFormSection>

            <AdminFormSection
              title="Payment / UPI"
              defaultOpen={false}
              hint="Provide either UPI ID or bank details. If UPI ID is entered, bank details are optional."
            >

            <div className="form-group">
              <label>UPI ID (VPA)</label>
              <input
                type="text"
                placeholder="e.g. salon@paytm, 9876543210@ybl"
                value={formData.payment?.upiId || ''}
                maxLength={80}
                className={formErrors.upiId ? 'input-error' : ''}
                onBlur={handleUpiBlur}
                onChange={(e) => {
                  const next = String(e.target.value || '').slice(0, 80);
                  setFormData({
                    ...formData,
                    payment: { ...(formData.payment || {}), upiId: next }
                  });
                  if (formErrors.upiId) setFormErrors((prev) => ({ ...prev, upiId: '' }));
                }}
              />
              <small style={{ color: '#8189A5', fontSize: '12px' }}>Used to generate UPI payment QR</small>
              {formErrors.upiId && <span className="form-error-text">{formErrors.upiId}</span>}
            </div>
            <div className="form-group">
              <label>Account / Payee Name</label>
              <input
                type="text"
                placeholder="Name shown on QR and receipts"
                value={formData.payment?.accountName || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  payment: { ...(formData.payment || {}), accountName: e.target.value }
                })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Bank Account Number (optional)</label>
                <input
                  type="text"
                  placeholder="Account number"
                  value={formData.payment?.accountNumber || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment: { ...(formData.payment || {}), accountNumber: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>IFSC Code (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. SBIN0001234"
                  value={formData.payment?.ifscCode || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment: { ...(formData.payment || {}), ifscCode: e.target.value }
                  })}
                />
              </div>
            </div>

            </AdminFormSection>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingSalon ? 'Update Branch' : 'Create Branch'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showMappingPanel && mappingSalon && (
        <div className="admin-card" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, fontSize: '16px', color: '#FFFFFF', marginBottom: '12px' }}>
            Map Services to Branch: {mappingSalon.name}
          </h2>
          <p style={{ marginTop: 0, marginBottom: '12px' }}>
            Select one or more services. Existing mapping will be replaced with this selection.
          </p>
          <div className="form-group">
            <label>Services (multi-select)</label>
            <select
              multiple
              size={Math.min(12, Math.max(6, allServices.length))}
              value={selectedServiceIds.map(String)}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions).map((option) => option.value);
                setSelectedServiceIds(values);
              }}
            >
              {allServices.map((service) => (
                <option key={String(service._id)} value={String(service._id)}>
                  {service.name} - {service.category?.name || 'No Category'}
                </option>
              ))}
            </select>
            <small style={{ color: '#8189A5', fontSize: '12px' }}>
              Hold Ctrl/Cmd to select multiple options, or use the checkboxes below.
            </small>
          </div>

          <div className="admin-list" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', marginTop: '12px' }}>
            {allServices.map((service) => {
              const sid = String(service._id ?? '');
              const isChecked = selectedServiceIds.some((s) => String(s) === sid);
              return (
                <label key={sid} className="admin-card" style={{ cursor: 'pointer', padding: '12px' }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleServiceSelection(sid)}
                    aria-label={`Map service: ${service.name}`}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>{service.name}</strong>
                  <div style={{ fontSize: '12px', color: '#8189A5', marginTop: '4px' }}>
                    {service.category?.name || 'No Category'} | INR {service.price}
                  </div>
                </label>
              );
            })}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleSaveServiceMapping} disabled={mappingLoading}>
              {mappingLoading ? 'Saving...' : 'Save Mapping'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeServiceMapping} disabled={mappingLoading}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <AdminListViewControls
        viewMode={listView.viewMode}
        onViewModeChange={listView.setViewMode}
        selectedCount={listView.selectedCount}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        totalCount={salons.length}
            showSelection={false}
      />
      <AdminSelectableList
        items={salons}
        getItemId={(salon) => salon._id}
        viewMode={listView.viewMode}
            selectable={false}
        isSelected={listView.isSelected}
        onToggleItem={listView.toggleItem}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        columns={salonColumns}
        tableClassName="salon-table"
        renderGridCard={(salon) => {
          const displayBranchCode = getDisplayBranchCode(salon);
          return (
            <div className="admin-card admin-card--stretch">
              <div className="card-header">
                <div>
                  <h3>{salon.name}</h3>
                  <p>
                    <strong>Branch ID:</strong> {displayBranchCode || 'N/A'}{' '}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleCopySalonId(displayBranchCode || salon._id)}
                    >
                      Copy
                    </button>
                  </p>
                  <p><strong>Brand:</strong> {salon.brand?.name || 'N/A'}</p>
                  {salon.branch && <p><strong>Branch / Location:</strong> {salon.branch}</p>}
                  <p>
                    <strong>Address:</strong>{' '}
                    <ExpandableText
                      text={[
                        salon.address?.street,
                        salon.address?.city,
                        salon.address?.state,
                        salon.address?.zipCode,
                        salon.address?.country
                      ]
                        .filter(Boolean)
                        .join(', ')}
                      lines={2}
                    />
                  </p>
                  <p>
                    <strong>Contact:</strong>{' '}
                    <ExpandableText
                      text={[salon.contact?.phone, salon.contact?.email].filter(Boolean).join(' | ')}
                      lines={1}
                    />
                  </p>
                  {salon.payment?.upiId && (
                    <p>
                      <strong>UPI ID:</strong>{' '}
                      <ExpandableText
                        text={`${salon.payment.upiId}${salon.payment.accountName ? ` (${salon.payment.accountName})` : ''}`}
                        lines={1}
                      />
                    </p>
                  )}
                  <p><strong>Mirrors:</strong> {salon.mirrors?.length || 0}</p>
                  <p><strong>Mapped Services:</strong> {getMappedServiceCount(salon._id)}</p>
                </div>
                <div className="card-actions">
                  {canManageBranchAdmins && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => openBranchAdmin(salon)}
                    >
                      Admin Login
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openServiceMapping(salon)}
                  >
                    Map Services
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEdit(salon)}
                  >
                    Edit
                  </button>
                  {canDeleteBranch && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(salon._id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        }}
        emptyState={salonEmptyState}
        scrollRef={scrollRef}
        footer={salonListFooter}
      />
      </>
      )}

      <PortalAdminCredentialsModal
        open={Boolean(adminTarget)}
        title={adminData?.id ? 'Update Branch Admin' : 'Add Branch Admin'}
        subtitle={
          adminTarget
            ? `Login for branch: ${adminTarget.name || adminTarget.branch || 'Branch'}${
                adminTarget.brand?.name ? ` (${adminTarget.brand.name})` : ''
              }`
            : ''
        }
        loading={adminLoading}
        saving={adminSaving}
        initialAdmin={adminData}
        onClose={closeBranchAdmin}
        onSave={saveBranchAdmin}
      />
      </div>
    </AdminLayout>
  );
};

export default SalonManagement;
